import { useEffect, useState } from 'react';
import {
  AlertCircle,
  AlertOctagon,
  ChevronUp,
  GripVertical,
  Loader2,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  EQUIPEMENT_CRITICITES,
  type EquipementCriticite,
  type EquipementStatut,
  type EquipementType,
} from '@mygmao/shared';
import { api, extractErrorMessage } from '../../lib/api';
import { ModalPortal } from '../ModalPortal';
import { usePermissions } from '../../hooks/usePermissions';
import { CriticiteBadge, StatutEquipementBadge } from '../equipements/badges';

/**
 * Panel "Équipements critiques" d'un service (US-SVC-07 + US-TR-07).
 *
 * Liste les équipements marqués critiques pour ce service avec leur niveau
 * (vital/critique/important/standard) et un ordre de priorité de
 * remplacement. Permet d'ajouter, de supprimer, et de réordonner par
 * drag-drop (toggle "Réorganiser" — bouton GripVertical).
 *
 * Le drag-drop utilise `@dnd-kit/sortable`. À la fin du drag, on POSTe le
 * nouvel ordre au backend via `PUT /services/:id/equipements-critiques/reorder`
 * (body : `{ order: string[] }` — IDs ServiceEquipementCritique).
 */

interface CritiqueRow {
  id: string;
  service_id: string;
  equipement_id: string;
  niveau_criticite: EquipementCriticite;
  priorite_remplacement: number | null;
  equipement: {
    id: string;
    code_equipement: string;
    designation: string;
    type: EquipementType;
    statut: EquipementStatut;
    criticite: EquipementCriticite | null;
  };
}

interface EquipementOption {
  id: string;
  code_equipement: string;
  designation: string;
  criticite: EquipementCriticite | null;
}

export function EquipementsCritiquesPanel({ serviceId }: { serviceId: string }) {
  const { can } = usePermissions();
  const canEdit = can('services', 'update');

  const [items, setItems] = useState<CritiqueRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [reorderMode, setReorderMode] = useState(false);
  const [reordering, setReordering] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Petite distance d'activation pour éviter qu'un simple clic ne déclenche
      // un drag (notamment sur le bouton "Retirer").
      activationConstraint: { distance: 5 },
    }),
  );

  async function refresh() {
    setLoading(true);
    try {
      const res = await api.get<{ data: CritiqueRow[] }>(
        `/services/${serviceId}/equipements-critiques`,
      );
      setItems(res.data.data);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId]);

  async function handleDelete(id: string) {
    if (!confirm('Retirer cet équipement de la liste des critiques ?')) return;
    try {
      await api.delete(`/services/equipements-critiques/${id}`);
      refresh();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    }
  }

  async function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((it) => it.id === active.id);
    const newIndex = items.findIndex((it) => it.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(items, oldIndex, newIndex);
    // Mise à jour optimiste : recalcul des priorités locales avant requête.
    setItems(next.map((it, idx) => ({ ...it, priorite_remplacement: idx + 1 })));
    setReordering(true);
    setErrorMessage(null);
    try {
      await api.put(`/services/${serviceId}/equipements-critiques/reorder`, {
        order: next.map((it) => it.id),
      });
      // On rafraîchit pour refléter les priorités calculées par le backend.
      refresh();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
      // Rollback en cas d'erreur.
      refresh();
    } finally {
      setReordering(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-6">
        <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      {canEdit && (
        <div className="flex justify-end gap-2 flex-wrap">
          {items.length > 1 && (
            <button
              onClick={() => setReorderMode((s) => !s)}
              className={`inline-flex items-center px-3 py-1.5 border rounded-lg text-sm font-medium ${
                reorderMode
                  ? 'border-primary-300 bg-primary-50 text-primary-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
              title="Réordonner par drag-drop"
            >
              <GripVertical className="w-3.5 h-3.5 mr-1" />
              {reorderMode ? 'Terminer la réorganisation' : 'Réorganiser'}
              {reordering && <Loader2 className="w-3 h-3 ml-1.5 animate-spin" />}
            </button>
          )}
          <button
            onClick={() => setAddOpen(true)}
            className="inline-flex items-center px-3 py-1.5 bg-primary-500 hover:bg-primary-600 text-white rounded-lg text-sm font-medium"
          >
            <Plus className="w-4 h-4 mr-1" />
            Ajouter un équipement critique
          </button>
        </div>
      )}

      {items.length === 0 ? (
        <p className="text-sm text-gray-500 italic">
          Aucun équipement critique déclaré pour ce service.
        </p>
      ) : reorderMode ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={items.map((it) => it.id)} strategy={verticalListSortingStrategy}>
            <ul className="space-y-2">
              {items.map((c) => (
                <SortableRow key={c.id} row={c} canEdit={canEdit} onDelete={handleDelete} />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      ) : (
        <ul className="space-y-2">
          {items.map((c) => (
            <StaticRow key={c.id} row={c} canEdit={canEdit} onDelete={handleDelete} />
          ))}
        </ul>
      )}

      {addOpen && (
        <AddCritiqueModal
          serviceId={serviceId}
          existingEquipementIds={items.map((i) => i.equipement_id)}
          onClose={() => setAddOpen(false)}
          onSaved={() => {
            setAddOpen(false);
            refresh();
          }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function StaticRow({
  row,
  canEdit,
  onDelete,
}: {
  row: CritiqueRow;
  canEdit: boolean;
  onDelete(id: string): void;
}) {
  return (
    <li className="rounded-lg border border-gray-200 p-3 bg-white flex items-start justify-between gap-2">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-900 flex items-center flex-wrap gap-2">
          <AlertOctagon className="w-3.5 h-3.5 text-red-500" />
          <span className="text-primary-600 text-xs font-semibold uppercase">
            {row.equipement.code_equipement}
          </span>
          {row.equipement.designation}
          <CriticiteBadge criticite={row.niveau_criticite} />
          <StatutEquipementBadge statut={row.equipement.statut} />
        </p>
        {row.priorite_remplacement != null && (
          <p className="text-xs text-gray-600 mt-1 flex items-center">
            <ChevronUp className="w-3 h-3 text-gray-400 mr-1" />
            Priorité remplacement : {row.priorite_remplacement}
          </p>
        )}
      </div>
      {canEdit && (
        <button
          onClick={() => onDelete(row.id)}
          className="p-1.5 rounded text-red-600 hover:bg-red-50"
          aria-label="Retirer"
          title="Retirer"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
    </li>
  );
}

function SortableRow({
  row,
  canEdit,
  onDelete,
}: {
  row: CritiqueRow;
  canEdit: boolean;
  onDelete(id: string): void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`rounded-lg border p-3 bg-white flex items-start justify-between gap-2 ${
        isDragging ? 'border-primary-400 shadow-lg cursor-grabbing' : 'border-gray-200'
      }`}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="p-1 -m-1 rounded text-gray-400 hover:text-gray-700 cursor-grab active:cursor-grabbing flex-shrink-0"
        aria-label="Glisser pour réordonner"
        title="Glisser pour réordonner"
      >
        <GripVertical className="w-4 h-4" />
      </button>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-900 flex items-center flex-wrap gap-2">
          <AlertOctagon className="w-3.5 h-3.5 text-red-500" />
          <span className="text-primary-600 text-xs font-semibold uppercase">
            {row.equipement.code_equipement}
          </span>
          {row.equipement.designation}
          <CriticiteBadge criticite={row.niveau_criticite} />
          <StatutEquipementBadge statut={row.equipement.statut} />
        </p>
        {row.priorite_remplacement != null && (
          <p className="text-xs text-gray-600 mt-1 flex items-center">
            <ChevronUp className="w-3 h-3 text-gray-400 mr-1" />
            Priorité remplacement : {row.priorite_remplacement}
          </p>
        )}
      </div>
      {canEdit && (
        <button
          onClick={() => onDelete(row.id)}
          className="p-1.5 rounded text-red-600 hover:bg-red-50 flex-shrink-0"
          aria-label="Retirer"
          title="Retirer"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
    </li>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function AddCritiqueModal({
  serviceId,
  existingEquipementIds,
  onClose,
  onSaved,
}: {
  serviceId: string;
  existingEquipementIds: string[];
  onClose(): void;
  onSaved(): void;
}) {
  const [equipements, setEquipements] = useState<EquipementOption[]>([]);
  const [equipementId, setEquipementId] = useState('');
  const [niveau, setNiveau] = useState<EquipementCriticite>('critique');
  const [priorite, setPriorite] = useState('');
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Charge en priorité les équipements rattachés au service, puis fallback global.
  useEffect(() => {
    api
      .get<{ data: EquipementOption[] }>(`/equipements?service_id=${serviceId}&page_size=200`)
      .then((res) => setEquipements(res.data.data))
      .catch(() => setEquipements([]));
  }, [serviceId]);

  const options = equipements.filter((e) => !existingEquipementIds.includes(e.id));

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!equipementId) {
      setErrorMessage('Sélectionnez un équipement.');
      return;
    }
    setSaving(true);
    setErrorMessage(null);
    try {
      await api.post(`/services/${serviceId}/equipements-critiques`, {
        equipement_id: equipementId,
        niveau_criticite: niveau,
        priorite_remplacement: priorite ? Number(priorite) : null,
      });
      onSaved();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
        <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Ajouter un équipement critique</h3>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100"
              aria-label="Fermer"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          <form onSubmit={submit} className="p-4 space-y-3">
            {errorMessage && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Équipement *</label>
              <select
                value={equipementId}
                onChange={(e) => setEquipementId(e.target.value)}
                className={inputClass}
                required
              >
                <option value="">— Sélectionnez —</option>
                {options.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.code_equipement} — {o.designation}
                  </option>
                ))}
              </select>
              {options.length === 0 && (
                <p className="text-xs text-gray-500 mt-1 italic">
                  Aucun équipement disponible pour ce service.
                </p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Niveau de criticité *
              </label>
              <select
                value={niveau}
                onChange={(e) => setNiveau(e.target.value as EquipementCriticite)}
                className={inputClass}
              >
                {EQUIPEMENT_CRITICITES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Priorité de remplacement (entier, optionnel — plus petit = plus prioritaire)
              </label>
              <input
                type="number"
                min="0"
                value={priorite}
                onChange={(e) => setPriorite(e.target.value)}
                className={inputClass}
              />
            </div>
            <div className="flex justify-end space-x-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={saving || options.length === 0}
                className="inline-flex items-center px-4 py-2 rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
              >
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Ajouter
              </button>
            </div>
          </form>
        </div>
      </div>
    </ModalPortal>
  );
}

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500';
